import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  MARITAL_STATUS_EVENT,
  isMarriedUser,
} from "../constants/maritalStatus";
import { assetUrl } from "../utils/api";
import { formatCityLabel } from "../utils/resolveRegion";

export default function CenterHome() {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const currentUserId = localStorage.getItem("user_id");

  const [user, setUser] = useState(null);
  const [friendCount, setFriendCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileViewCount, setProfileViewCount] = useState(0);

  const formatYearsAgo = (dateString) => {
    if (!dateString) return "未记录";

    const date = new Date(dateString);
    const now = new Date();

    let years = now.getFullYear() - date.getFullYear();
    const monthDiff = now.getMonth() - date.getMonth();
    const dayDiff = now.getDate() - date.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      years -= 1;
    }

    return years <= 0 ? "1年内" : `${years}年前`;
  };

  const refreshDashboard = async () => {
    if (!currentUserId) return;

    try {
      const res = await fetch(`/api/profile/${currentUserId}`);
      const data = await res.json();

      if (res.ok) {
        setUser(data);

        const married = isMarriedUser(data.marital_status);
        if (married) {
          setFriendCount(0);
        } else {
          const friendsRes = await fetch(
            `/api/friends?current_user_id=${currentUserId}`
          );
          const friendsData = await friendsRes.json();
          if (Array.isArray(friendsData)) {
            setFriendCount(friendsData.length);
          }
        }
      }
    } catch (err) {
      console.error("加载用户资料失败:", err);
    }

    try {
      const res = await fetch(
        `/api/unread-count?current_user_id=${currentUserId}`
      );
      const data = await res.json();
      if (res.ok) {
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error("加载未读消息失败:", err);
    }

    try {
      const viewRes = await fetch(
        `/api/profile-views/mine/count?user_id=${currentUserId}&days=30`
      );
      const viewData = await viewRes.json();
      if (viewRes.ok) {
        setProfileViewCount(viewData.unique_viewers || 0);
      }
    } catch (err) {
      console.error("加载来访统计失败:", err);
    }
  };

  useEffect(() => {
    if (!currentUserId) {
      navigate("/login");
      return;
    }

    refreshDashboard();
  }, [currentUserId, navigate, outletContext.maritalStatus]);

  useEffect(() => {
    const handleMaritalUpdate = () => {
      refreshDashboard();
    };

    window.addEventListener(MARITAL_STATUS_EVENT, handleMaritalUpdate);
    return () =>
      window.removeEventListener(MARITAL_STATUS_EVENT, handleMaritalUpdate);
  }, [currentUserId]);

  if (!user) {
    return <div className="text-center text-gray-400 py-10">加载中...</div>;
  }

  const married =
    outletContext.isMarried || isMarriedUser(user.marital_status);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <img
          src={user.avatar ? assetUrl(user.avatar) : "https://via.placeholder.com/80"}
          alt={user.name}
          className="w-20 h-20 rounded-full object-cover"
        />

        <div>
          <h2 className="text-2xl font-bold">{user.name || "未填写姓名"}</h2>
          <p className="text-gray-500">
            {user.age || "?"}岁 · {formatCityLabel(user.province, user.city, user.district, "未填写城市")}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {user.marital_status || "未填写婚姻状态"} ·{" "}
            {user.education || "未填写学历"}
          </p>
        </div>
      </div>

      {!user.email_verified && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-medium text-amber-900">建议绑定邮箱</p>
            <p className="text-sm text-amber-800 mt-1">
              绑定邮箱后可使用邮箱登录，并可通过邮件找回密码，账号更安全。
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/center/security")}
            className="shrink-0 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold"
          >
            去绑定邮箱
          </button>
        </div>
      )}

      <div
        className={`rounded-2xl p-5 space-y-2 ${
          married ? "bg-rose-50" : "bg-pink-50"
        }`}
      >
        <p className="text-gray-700">
          注册时间：{formatYearsAgo(user.created_at)}
        </p>
        <p className="text-gray-700">
          您当前为：{user.is_id_verified ? "认证会员" : "普通会员"}
        </p>
        <p className="text-gray-700">
          {married
            ? "服务方向：婚姻修复 · 关系咨询 · 家庭成长"
            : "服务方向：寻找对象 · AI 匹配 · 交友互动"}
        </p>
      </div>

      {married ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-rose-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">资料完成</p>
              <p className="text-lg font-bold">
                {user.profile_completed ? "已完成" : "未完成"}
              </p>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">关系测评</p>
              <p className="text-lg font-bold">
                {user.has_questionnaire ? "已测评" : "未测评"}
              </p>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">未读消息</p>
              <p className="text-lg font-bold">{unreadCount}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/ai/consultant")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">AI 婚姻顾问</p>
              <p className="text-xs text-gray-400 mt-1">沟通修复 · 结婚证常识</p>
            </button>
            <button
              onClick={() => navigate("/ai/personality")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">我的 AI 人格</p>
              <p className="text-xs text-gray-400 mt-1">雷达图 · 成长方向</p>
            </button>
            <button
              onClick={() => navigate("/ai/twin")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">AI 数字分身</p>
              <p className="text-xs text-gray-400 mt-1">和另一个自己对话</p>
            </button>
            <button
              onClick={() => navigate("/center/ai-memory")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">我的 AI 记忆</p>
              <p className="text-xs text-gray-400 mt-1">查看与管理</p>
            </button>
            <button
              onClick={() => navigate("/consultants")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">预约咨询师</p>
              <p className="text-xs text-gray-400 mt-1">婚姻修复 · 情感咨询</p>
            </button>
            <button
              onClick={() => navigate("/questionnaire")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">关系测评</p>
              <p className="text-xs text-gray-400 mt-1">了解当前关系状态</p>
            </button>
            <button
              onClick={() => navigate("/center/profile")}
              className="bg-white border rounded-xl p-4 hover:bg-rose-50 transition text-left"
            >
              <p className="font-semibold text-gray-800">资料设置</p>
              <p className="text-xs text-gray-400 mt-1">完善婚姻相关背景</p>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">资料完成</p>
              <p className="text-lg font-bold">
                {user.profile_completed ? "已完成" : "未完成"}
              </p>
            </div>
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">测评状态</p>
              <p className="text-lg font-bold">
                {user.has_questionnaire ? "已测评" : "未测评"}
              </p>
            </div>
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">谁看了我</p>
              <p className="text-lg font-bold">{profileViewCount}</p>
            </div>
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">我的好友</p>
              <p className="text-lg font-bold">{friendCount}</p>
            </div>
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">未读消息</p>
              <p className="text-lg font-bold">{unreadCount}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <button
              onClick={() => navigate("/ai/consultant")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">AI咨询师</p>
              <p className="text-xs text-gray-400 mt-1">婚恋画像</p>
            </button>
            <button
              onClick={() => navigate("/ai/personality")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">AI人格</p>
              <p className="text-xs text-gray-400 mt-1">雷达档案</p>
            </button>
            <button
              onClick={() => navigate("/ai/twin")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">数字分身</p>
              <p className="text-xs text-gray-400 mt-1">另一个自己</p>
            </button>
            <button
              onClick={() => navigate("/ai/match")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">AI匹配</p>
              <p className="text-xs text-gray-400 mt-1">深度报告</p>
            </button>
            <button
              onClick={() => navigate("/ai/simulation")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">关系模拟</p>
              <p className="text-xs text-gray-400 mt-1">双方AI议题预演</p>
            </button>
            <button
              onClick={() => navigate("/center/ai-memory")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition text-left"
            >
              <p className="font-semibold">AI记忆</p>
              <p className="text-xs text-gray-400 mt-1">查看与管理</p>
            </button>
            <button
              onClick={() => navigate("/center/profile")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition"
            >
              资料设置
            </button>
            <button
              onClick={() => navigate("/center/views")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition"
            >
              谁看了我
            </button>
            <button
              onClick={() => navigate("/discover")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition"
            >
              寻找对象
            </button>
            <button
              onClick={() => navigate("/chat-list")}
              className="bg-white border rounded-xl p-4 hover:bg-pink-50 transition"
            >
              消息列表
            </button>
          </div>
        </>
      )}

      <div>
        <h3 className="font-semibold mb-2">
          {married ? "情况说明" : "自我介绍"}
        </h3>
        <p className="text-gray-600">
          {user.bio ||
            (married
              ? "建议补充您的婚姻背景、当前困扰和咨询诉求，便于咨询师了解您。"
              : "还没有填写自我介绍")}
        </p>
      </div>
    </div>
  );
}
